import { makeAutoObservable, runInAction } from 'mobx';
import apiManage from '../../request';
import type {
    AnagraficaOcrListQuery,
    AnagraficaOcrSessionListItem,
} from '../../component/archive-form/anagrafica-ocr';
import type { ConditionType } from '../../component/form/search-key-input.component';

/**
 * 超管 OCR 会话列表页状态。
 */
export class AnagraficaOcrSessionsState {
    /** 表格行。 */
    list: AnagraficaOcrSessionListItem[] = [];

    /** 总条数。 */
    dataTotal = 0;

    /** 表格 loading。 */
    isTableLoading = false;

    /** 当前页（从 1 起，对接后端 page）。 */
    page = 1;

    /** 每页条数。 */
    pageSize = 20;

    /** SearchKeyInput 条件。 */
    conditions: ConditionType[] = [];

    /** 详情弹窗是否打开。 */
    detailOpen = false;

    /** 当前查看的会话。 */
    detailSession: AnagraficaOcrSessionListItem | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * 页面挂载时拉第一页。
     */
    async init(): Promise<void> {
        this.page = 1;
        await this.fetchList();
    }

    /**
     * 从筛选条件拼列表 query。
     */
    buildQuery(): AnagraficaOcrListQuery {
        const query: AnagraficaOcrListQuery = {
            page: this.page,
            page_size: this.pageSize,
        };
        this.conditions.forEach((item) => {
            if (item.key === 'nickname' && item.value) {
                query.nickname = String(item.value).trim();
            }
            if (item.key === 'string_id' && item.value) {
                query.string_id = String(item.value).trim();
            }
        });
        return query;
    }

    /**
     * 请求会话列表。
     */
    async fetchList(): Promise<void> {
        this.isTableLoading = true;
        const result = await apiManage.list_anagrafica_ocr(this.buildQuery());
        runInAction(() => {
            this.isTableLoading = false;
            if (result && !result.error) {
                this.list = result.items ?? [];
                this.dataTotal = Number(result.total) || 0;
                if (result.page) {
                    this.page = Number(result.page) || this.page;
                }
                if (result.page_size) {
                    this.pageSize = Number(result.page_size) || this.pageSize;
                }
            } else {
                this.list = [];
                this.dataTotal = 0;
            }
        });
    }

    /**
     * 更新筛选并回到第 1 页。
     * @param conditions SearchKeyInput 条件
     */
    async applySearch(conditions: ConditionType[]): Promise<void> {
        this.conditions = [...conditions];
        this.page = 1;
        await this.fetchList();
    }

    /**
     * 分页变更（TableComponent 的 page 从 0 起）。
     * @param params 页码与每页条数
     */
    async changePage(params: { page: number; curRowsPerPage: number }): Promise<void> {
        this.page = params.page + 1;
        this.pageSize = params.curRowsPerPage;
        await this.fetchList();
    }

    /**
     * 打开会话字段详情。
     * @param row 列表行
     */
    openDetail(row: AnagraficaOcrSessionListItem): void {
        this.detailSession = row;
        this.detailOpen = true;
    }

    /**
     * 关闭详情弹窗。
     */
    closeDetail(): void {
        this.detailOpen = false;
        this.detailSession = null;
    }
}
